<template>

    <ThemeChanger :to="Theme.Purple">

        <Container fluid class="pt-32 md:pt-48 pb-24">

            <PageHeader header="Join the DC development team"/>

            <Grid class="mt-8">

                <Column span="4">

                    <AOS order="3">

                        <Paragraph class="text-gray-600">

                            <span>
                                We are committed to not only creating best-in-class digital products,
                                but also to maximizing our employee experience and their personal development.
                            </span>

                        </Paragraph>

                    </AOS>

                </Column>

            </Grid>

            <Grid :columns="[ 2, 6 ]" class="mt-12 md:mt-24">

                <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]">

                    <form v-if="!formSubmitted" class="w-full" @submit.prevent="submitForm">

                        <Input :type="InputType.Text"
                               :validation="validation.name"
                               name="name"
                               :placeholder="lang.name"/>

                        <Input :type="InputType.Email"
                               :validation="validation.email"
                               name="email"
                               :placeholder="lang.email"/>

                        <Input :type="InputType.Text"
                               :validation="validation.githubUsername"
                               name="githubUsername"
                               placeholder="Github username"/>

                        <div class="mt-12 md:mt-16 font-bold font-mabry-pro text-lg md:text-2xl">
                            I'm a...
                        </div>

                        <CheckboxInput class="mt-8"
                                       :checked="selectedRole === RoleType.VueJs"
                                       title="Front-end developer (Vue.js)"
                                       @checked="selectedRole = RoleType.VueJs"/>

                        <CheckboxInput class="mt-8"
                                       :checked="selectedRole === RoleType.Flutter"
                                       title="Flutter developer"
                                       @checked="selectedRole = RoleType.Flutter"/>

                        <CheckboxInput class="mt-8"
                                       :checked="selectedRole === RoleType.Laravel"
                                       title="Laravel developer"
                                       @checked="selectedRole = RoleType.Laravel"/>

                        <CheckboxInput class="mt-8"
                                       :checked="selectedRole === RoleType.MiniProgram"
                                       title="Mini Program developer"
                                       @checked="selectedRole = RoleType.MiniProgram"/>

                        <CheckboxInput class="mt-8"
                                       :checked="selectedRole === RoleType.ProjectManager"
                                       title="Project manager"
                                       @checked="selectedRole = RoleType.ProjectManager"/>

                        <Input class="mt-12 md:mt-16"
                               :type="InputType.TextArea"
                               :validation="validation.yourDetails"
                               name="yourDetails"
                               placeholder="Tell us about yourself..."/>

                        <div class="flex flex-col md:flex-row justify-between mt-16 md:mt-20">

                            <FileUpload text="Upload a resume if you have it..." @selected="setResume"/>

                            <Button class="mt-8 md:mt-0" :is-loading="formLoading" large>

                                {{ formLoading ? lang.submitting : lang.submit }}

                            </Button>

                        </div>

                    </form>

                    <div v-else class="flex justify-center items-center flex-col">

                        <Checkmark/>

                        <Paragraph class="text-green-200 mt-10 w-full md:w-3/6 text-center">
                            {{ lang.message_sent }}
                        </Paragraph>

                        <Button class="mt-12" large @click="formSubmitted = false">

                            {{ lang.sent_button_text }}

                        </Button>

                    </div>

                </Column>

            </Grid>

        </Container>

    </ThemeChanger>

    <MetaInfo v-if="lang.seo" :seo="lang.seo" :structured-data="lang.seo_structured_data"/>

</template>

<script lang="ts">

    import { defineComponent, ref } from 'vue'
    import Button from '../components/Button.vue'
    import Container from '../components/Container.vue'
    import FileUpload from '../components/Form/FileUpload.vue'
    import CheckboxInput from '../components/Form/CheckboxInput.vue'
    import Input from '../components/Form/Input.vue'
    import Column from '../components/Grid/Column.vue'
    import Grid from '../components/Grid/Grid.vue'
    import Checkmark from '../components/Icons/Checkmark.vue'
    import PageHeader from '../components/PageHeader.vue'
    import Paragraph from '../components/Paragraph.vue'
    import ThemeChanger from '../components/ThemeChanger.vue'
    import { useValidation } from '../compositions/form/UseValidation'
    import { email, required } from '../compositions/form/UseValidators'
    import { useApi } from '../compositions/UseApi'
    import { currentActiveBreakPoint } from '../compositions/UseResponsiveValue'
    import { useTranslation } from '../compositions/UseTranslation'
    import { Breakpoint } from '../enums/Breakpoint'
    import { Theme } from '../enums/Theme'
    import { RoleType } from '../enums/RoleType'
    import { InputType } from '../enums/InputType'
    import { JobEnquiryInterface } from '../Interfaces/EnquiryInterface'
    import { useTheme } from '../compositions/UseTheme'
    import MetaInfo from '../components/MetaInfo.vue'

    export default defineComponent({
        name: 'WorkWithUs',
        components: {
            MetaInfo,
            Container,
            PageHeader,
            Grid,
            Column,
            Input,
            Paragraph,
            Button,
            CheckboxInput,
            FileUpload,
            Checkmark,
            ThemeChanger
        },
        setup() {

            useTheme().initializeCurrentThemeBasedOnRouteMeta()

            const { contact } = useTranslation()
            const lang = contact.value
            const formSubmitted = ref(false)
            const formLoading = ref(false)

            let resume = null as File | null

            const { validation, validate, getData } = useValidation({
                name: [ required(lang.name_required) ],
                email: [ required(lang.email_required), email(lang.email_valid) ],
                githubUsername: [],
                yourDetails: []
            })

            const selectedRole = ref(RoleType.VueJs)

            return {
                lang: contact,
                currentActiveBreakPoint,
                Breakpoint,
                Theme,
                selectedRole,
                RoleType,
                formSubmitted,
                formLoading,
                InputType,
                validation,
                setResume(file: File) {

                    resume = file

                },
                async submitForm() {

                    if (!validate()) {

                        return

                    }

                    formLoading.value = true

                    const data = {
                        ...getData(),
                        role: selectedRole.value
                    } as JobEnquiryInterface

                    if (resume) {

                        data.resume = resume

                    }

                    const response = await useApi().jobEnquiry(data as JobEnquiryInterface)

                    if (response.isErr()) {

                        alert(response.error.message)
                        formLoading.value = false

                    }

                    if (response.isOk()) {

                        formSubmitted.value = true

                    }

                    formLoading.value = false

                }
            }

        }
    })

</script>
